'use client';

import React, { useEffect, useRef, useState } from 'react';

import { Button, Typography } from 'lighthouse';

import { InfiniteScrollList } from '@/widgets/InfiniteScrollList';
import { ReviewCard } from '@/widgets/ReviewCard';

import { ReviewsSkeleton } from '../../../../../app/(Main)/vendor/[slug]/_components/VendorTabs/_components/ReviewsTab/components/ReviewSkeleton';
import { ReviewsSectionProps } from './models';
import { RatingSummary } from './RatingSummary';

import { getReviewsByreferenceTypeService } from '@/client/services/review/methods';
import { ReviewResponseType } from '@/client/services/review/models';
import { MessageText1, Star1 } from 'iconsax-react';

export const ReviewsSection = ({ uuid, onAddReview, canAddReview, onSummaryChange, hideHeader = false }: ReviewsSectionProps) => {
    const [lastPage, setLastPage] = useState(false);
    const [initialReviews, setInitialReviews] = useState<ReviewResponseType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const hasFetched = useRef(false);
    const sectionRef = useRef<HTMLElement | null>(null);

    const updateSummary = (reviews: ReviewResponseType[], totalCount = reviews.length) => {
        if (!onSummaryChange) return;
        if (!reviews.length) {
            onSummaryChange(null);
            return;
        }
        const sum = reviews.reduce((acc, review) => acc + review.rate, 0);
        onSummaryChange({ average: sum / reviews.length, count: totalCount });
    };

    const fetchFirstPage = () => {
        if (hasFetched.current) return;
        hasFetched.current = true;
        setIsLoading(true);
        getReviewsByreferenceTypeService({
            referenceType: 'PRODUCT_UUID',
            referenceId: uuid,
            page: 0,
            size: 10
        })
            .then((response) => {
                const reviews = response.data.content || [];
                setInitialReviews(reviews);
                setLastPage(response.data.last);
                updateSummary(reviews, response.data.totalElements ?? reviews.length);
            })
            .catch(() => {
                setLastPage(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        const element = sectionRef.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    fetchFirstPage();
                    observer.disconnect();
                }
            },
            { rootMargin: '300px' }
        );

        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    const fetchReviews = async (page: number) => {
        try {
            const response = await getReviewsByreferenceTypeService({
                referenceType: 'PRODUCT_UUID',
                referenceId: uuid,
                page,
                size: 10
            });
            setLastPage(response.data.last);
            return response.data.content || [];
        } catch {
            return [];
        }
    };

    return (
        <section id='reviews' ref={sectionRef} className='scroll-mt-20'>
            <div className={`mb-3 flex items-center ${hideHeader ? 'justify-start' : 'justify-between'}`}>
                {!hideHeader && (
                    <Typography variant='h6' color='blackHighEmphasis' component='h2'>
                        دیدگاه کاربران
                    </Typography>
                )}
            </div>

            {isLoading ? (
                <div className='flex flex-col gap-3'>
                    <ReviewsSkeleton />
                </div>
            ) : !initialReviews.length ? (
                <div className='relative isolate overflow-hidden rounded-2xl border border-solid border-gray-100 bg-N50 p-5 md:p-8'>
                    <div aria-hidden className='pointer-events-none absolute -left-12 -top-16 -z-10 h-56 w-56 rounded-full bg-primary/5' />
                    <div className='flex items-start justify-between gap-4 md:items-center md:gap-8'>
                        <div className='min-w-0 flex-1'>
                            <Typography
                                variant='h6'
                                component='h3'
                                fontWeight={700}
                                color='blackHighEmphasis'
                                className='!text-base !leading-7 md:!text-xl md:!leading-8'>
                                {canAddReview ? 'اولین دیدگاه این محصول را شما بنویسید' : 'هر تجربه، یک راهنمای تازه'}
                            </Typography>
                            <Typography variant='body2' color='blackMediumEmphasis' className='mt-2 max-w-md !leading-7'>
                                {canAddReview
                                    ? 'تجربه شما از این محصول می‌تواند راهنمای خرید دیگران باشد.'
                                    : 'هنوز دیدگاهی ثبت نشده؛ تجربه کاربران اینجا به انتخاب بهتر کمک خواهد کرد.'}
                            </Typography>
                        </div>
                        <div aria-hidden className='pointer-events-none relative mt-2 h-20 w-20 shrink-0 md:mt-0 md:h-28 md:w-28'>
                            <div className='absolute inset-1 rounded-full border border-solid border-primary/10 md:inset-2' />
                            <div className='absolute inset-2 rotate-[-8deg] rounded-2xl border border-solid border-primary/10 bg-primary/5 md:inset-3' />
                            <div
                                className='absolute inset-2 flex rotate-6 items-center justify-center rounded-2xl border border-solid border-white
                                    bg-white text-primary shadow-elevation1 md:inset-3'>
                                <MessageText1 size={36} color='currentColor' variant='Bulk' />
                            </div>
                            <div
                                className='absolute -bottom-1 -left-1 flex h-8 w-8 items-center justify-center rounded-xl border-2 border-solid
                                    border-N50 bg-amber-100 text-amber-600 md:bottom-0 md:left-0 md:h-9 md:w-9'>
                                <Star1 size={18} color='currentColor' variant='Bold' />
                            </div>
                        </div>
                    </div>
                    {canAddReview && (
                        <div className='mt-12 flex justify-end'>
                            <Button
                                className='!h-9 !min-h-0 w-auto !min-w-0 shrink-0 self-end !px-3.5 !py-1.5 !text-[13px] !leading-5 !shadow-none
                                    md:self-center'
                                variant='contained'
                                color='primary'
                                size='small'
                                startIcon={<MessageText1 size={16} color='currentColor' aria-hidden />}
                                onClick={onAddReview}>
                                نوشتن اولین دیدگاه
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <div className='flex flex-col gap-4'>
                    <RatingSummary reviews={initialReviews} />
                    {canAddReview && (
                        <div
                            className='flex flex-col gap-4 rounded-xl border border-solid border-primary/15 bg-primary/5 p-4 md:flex-row
                                md:items-center md:justify-between md:p-5'>
                            <div className='flex flex-col gap-1.5'>
                                <Typography variant='subtitle1' fontWeight={700} color='blackHighEmphasis'>
                                    شما چه تجربه‌ای از این محصول دارید؟
                                </Typography>
                                <Typography variant='body2' color='blackMediumEmphasis'>
                                    دیدگاه شما به دیگران کمک می‌کند انتخاب بهتری داشته باشند.
                                </Typography>
                            </div>
                            <Button
                                className='!h-9 !min-h-0 w-auto !min-w-0 shrink-0 self-end !px-3.5 !py-1.5 !text-[13px] !leading-5 !shadow-none
                                    md:self-center'
                                variant='contained'
                                color='primary'
                                size='small'
                                startIcon={<MessageText1 size={16} color='currentColor' aria-hidden />}
                                onClick={onAddReview}>
                                نوشتن دیدگاه
                            </Button>
                        </div>
                    )}
                    <InfiniteScrollList<ReviewResponseType>
                        fetchData={fetchReviews}
                        isLastPage={lastPage}
                        defaultItems={initialReviews}
                        renderRow={(review) => <ReviewCard key={review.id} review={review} />}
                        containerProps={{
                            className: 'flex flex-col gap-3'
                        }}
                        infiniteScrollProps={{
                            loader: (
                                <div className='flex flex-col gap-3'>
                                    <ReviewsSkeleton />
                                </div>
                            )
                        }}
                    />
                </div>
            )}
        </section>
    );
};
